import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './indent-sheet.css';
import { useQuery } from '@tanstack/react-query';
import { api, API_URL, getToken } from '../../lib/api';

/**
 * Every container of ONE indent, one row each, with everything that has happened to it on the
 * inspection side: the surveyor's Stage 3 inspection, the 3.1 / 3.2 sign-offs, the depot
 * gate-in / gate-out checks, the CRO and the stock entry. A plain bordered grid (like a
 * spreadsheet) — used under an indent row in the indents list and on the indent page.
 *
 * Photos and the inspection report open INSIDE the page (a strip under the row, a viewer for the
 * report, a large-photo view). Nothing here links out to Drive or the survey sheet.
 */
interface GateRef { fileId?: string; url?: string }
interface Gate { at: string; by: string; location: string; pti: string; repair: string; repairBudget: string; photos: Array<GateRef & { label: string }>; report: GateRef | null; remarks: string }
export interface Row {
  containerNo: string; label: string; productCode: string; status: string;
  survey: { at: string; surveyor: string; kind: string; condition: string; remarks: string; reportId: string; ok: number; issues: string[]; photos: Array<{ id: string; label: string }> } | null;
  approvals: Array<{ stage: string; name: string; status: string; at: string; comment: string }>;
  gateIn: Gate | null; gateOut: Gate | null;
  cro: { done: boolean; ref: string; destination: string };
  inStock: boolean; stockVia: string;
}
export interface Sheet {
  indentId: string; containers: Row[];
  indentWideApprovals: Array<{ stage: string; name: string; status: string; at: string; comment: string }>;
  totals: { containers: number; surveyed: number; withIssues: number; signedOff: number; gatedIn: number; croDone: number; inStock: number };
  sheetError: string;
}
/** What a cell can open: a photo strip or a report viewer, from the survey or the gate-in check. */
type Open = { key: string; kind: 'photos' | 'report' | 'gatephotos' | 'gatereport' } | null;
interface Media { path?: string; url?: string; label: string }

const d = (v: string) => { const t = String(v || '').slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(t) ? `${t.slice(8, 10)}/${t.slice(5, 7)}/${t.slice(2, 4)}` : (v ? String(v).slice(0, 10) : ''); };
const ok = (t: string) => <span className="font-semibold text-emerald-700">{t}</span>;
const bad = (t: string) => <span className="font-semibold text-rose-700">{t}</span>;
const warn = (t: string) => <span className="font-semibold text-amber-700">{t}</span>;
const none = <span className="text-slate-300">—</span>;

/** Fetch an authenticated file as a same-origin blob URL (an <img>/<iframe> cannot send the Bearer header). */
function useBlobUrl(path?: string): { src: string; failed: boolean } {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!path) return;
    let cancelled = false; let made = '';
    setSrc(''); setFailed(false);
    const token = getToken();
    fetch(`${API_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((b) => { if (cancelled) return; made = URL.createObjectURL(b); setSrc(made); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (made) URL.revokeObjectURL(made); };
  }, [path]);
  return { src, failed };
}

function Img({ m, className, onClick }: { m: Media; className?: string; onClick?: () => void }) {
  const blob = useBlobUrl(m.url ? undefined : m.path);
  const src = m.url || blob.src;
  if (!src) return <div className={`${className || ''} flex items-center justify-center bg-slate-100 text-[10px] text-slate-400`}>{blob.failed ? 'unavailable' : '…'}</div>;
  return <img src={src} alt={m.label} className={className} onClick={onClick} loading="lazy" />;
}

function Report({ m }: { m: Media }) {
  const blob = useBlobUrl(m.url ? undefined : m.path);
  const src = m.url || blob.src;
  if (!src) return <div className="p-3 text-[11px] text-slate-500">{blob.failed ? 'The report could not be loaded.' : 'Loading the report…'}</div>;
  return <iframe src={src} title={m.label} className="h-[460px] w-full border border-slate-300 bg-white" />;
}

function Lightbox({ m, onClose }: { m: Media; onClose: () => void }) {
  const big = useBlobUrl(m.url ? undefined : m.path);
  const src = m.url || big.src;
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div className="mb-2 text-sm font-semibold text-white">{m.label} <span className="ml-2 text-xs font-normal text-white/60">click anywhere or press Esc to close</span></div>
      {src ? <img src={src} alt={m.label} className="max-h-[85vh] max-w-[92vw] object-contain" /> : <div className="text-white/70">{big.failed ? 'Could not load the photo' : 'Loading…'}</div>}
    </div>,
    document.body,
  );
}

function signOff(r: Row, stage: '3.1' | '3.2') {
  const a = r.approvals.find((x) => x.stage.includes(stage));
  if (!a) return none;
  const good = /^approved$/i.test(a.status);
  return <span title={`${a.name}${a.at ? ` · ${d(a.at)}` : ''}${a.comment ? ` — ${a.comment}` : ''}`}>{good ? ok('✓') : bad('✗')} {a.name.split(' ')[0]}</span>;
}

/** The indent's per-container inspection records. One query key, so the sheet below and the
 *  plain summary above it on the indent page share a single request. */
export function useInspectionSheet(indentId: string) {
  return useQuery<Sheet>({
    queryKey: ['procurement', 'indent', indentId, 'inspection-sheet'],
    queryFn: () => api.get(`/api/procurement/indents/${encodeURIComponent(indentId)}/inspection-sheet`),
    staleTime: 60_000,
    enabled: !!indentId,
  });
}

export default function IndentInspectionSheet({ indentId }: { indentId: string }) {
  const q = useInspectionSheet(indentId);
  const [open, setOpen] = useState<Open>(null);
  const [big, setBig] = useState<Media | null>(null);
  if (q.isLoading) return <div className="p-2 text-[11px] text-slate-500">Reading the inspection records…</div>;
  if (q.isError || !q.data) return <div className="p-2 text-[11px] text-rose-700">Could not load the inspection details for {indentId}.</div>;
  const { containers, totals, indentWideApprovals, sheetError } = q.data;
  if (!containers.length) return <div className="p-2 text-[11px] text-slate-500">No containers on {indentId} yet.</div>;
  const other = (r: Row) => r.approvals.filter((a) => !a.stage.includes('3.1') && !a.stage.includes('3.2'));
  const media = (id: string, thumb = false) => `/api/procurement/indents/${encodeURIComponent(indentId)}/inspection-media/${id}${thumb ? '?size=thumb' : ''}`;
  const gateMedia = (g: GateRef): Media => (g.fileId ? { path: `/api/files/${g.fileId}`, label: '' } : { url: g.url, label: '' });
  const toggle = (key: string, kind: NonNullable<Open>['kind']) => setOpen((o) => (o && o.key === key && o.kind === kind ? null : { key, kind }));
  const btn = (active: boolean, text: string, onClick: () => void) => (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} className={`border px-1.5 py-px text-[10.5px] font-semibold ${active ? 'border-[#1b3a57] bg-[#1b3a57] text-white' : 'border-slate-300 bg-white text-[#1b3a57] hover:bg-slate-100'}`}>{text}</button>
  );

  return (
    <div className="isheet overflow-x-auto bg-white">
      <table>
        <thead>
          <tr>
            <th rowSpan={2}>#</th><th rowSpan={2}>Container</th><th rowSpan={2}>Code</th><th rowSpan={2}>Status</th>
            <th colSpan={6}>Surveyor inspection (Stage 3)</th><th colSpan={3}>Sign-off</th>
            <th colSpan={4}>Gate-in</th><th rowSpan={2}>Gate-out</th><th rowSpan={2}>CRO</th><th rowSpan={2}>Stock</th>
          </tr>
          <tr>
            <th>Date</th><th>By</th><th>Condition</th><th>OK</th><th>Issues</th><th>Photos / report</th>
            <th>3.1</th><th>3.2</th><th>Other</th>
            <th>Date</th><th>PTI</th><th>Repair</th><th>Photos / report</th>
          </tr>
        </thead>
        <tbody>
          {containers.map((r, idx) => {
            const sv = r.survey;
            const key = `${r.containerNo || r.label}|${idx}`;
            const isOpen = open?.key === key;
            const gi = r.gateIn;
            return (
              <Fragment key={key}>
                <tr>
                  <td className="text-slate-400">{idx + 1}</td>
                  <td className="mono">{r.containerNo || r.label}</td>
                  <td className="mono">{r.productCode || none}</td>
                  <td>{r.status || none}</td>
                  {sv ? (
                    <>
                      <td>{d(sv.at) || none}</td>
                      <td>{sv.surveyor || none}</td>
                      <td>{sv.condition || none}</td>
                      <td>{ok(String(sv.ok))}</td>
                      <td className="issues" title={sv.issues.join('\n')}>
                        {sv.issues.length ? <>{bad(`✗ ${sv.issues.length}`)} {sv.issues.slice(0, 3).join(' · ')}{sv.issues.length > 3 ? ` · +${sv.issues.length - 3}` : ''}</> : none}
                        {sv.remarks && <div className="italic text-slate-500">“{sv.remarks}”</div>}
                      </td>
                      <td>
                        <span className="inline-flex gap-1">
                          {sv.photos.length > 0 && btn(isOpen && open?.kind === 'photos', `Photos ${sv.photos.length}`, () => toggle(key, 'photos'))}
                          {sv.reportId && btn(isOpen && open?.kind === 'report', 'Report', () => toggle(key, 'report'))}
                          {!sv.photos.length && !sv.reportId ? none : null}
                        </span>
                      </td>
                    </>
                  ) : (
                    <td colSpan={6} className="text-slate-400">no survey filed</td>
                  )}
                  <td>{signOff(r, '3.1')}</td>
                  <td>{signOff(r, '3.2')}</td>
                  <td>{other(r).length ? other(r).map((a, i) => <span key={i} className="mr-1" title={a.name}>{/^approved$/i.test(a.status) ? ok('✓') : bad('✗')} {a.name.split(' ')[0]}</span>) : none}</td>
                  {gi ? (
                    <>
                      <td title={gi.location}>{d(gi.at) || none}</td>
                      <td>{/^yes$/i.test(gi.pti) ? ok('Yes') : gi.pti ? <span>{gi.pti}</span> : none}</td>
                      <td>{/^yes$/i.test(gi.repair) ? warn(`Needed${gi.repairBudget ? ` ₹${gi.repairBudget}` : ''}`) : gi.repair ? ok('None') : none}</td>
                      <td>
                        <span className="inline-flex gap-1">
                          {gi.photos.length > 0 && btn(isOpen && open?.kind === 'gatephotos', `Photos ${gi.photos.length}`, () => toggle(key, 'gatephotos'))}
                          {gi.report && btn(isOpen && open?.kind === 'gatereport', 'Report', () => toggle(key, 'gatereport'))}
                          {!gi.photos.length && !gi.report ? none : null}
                        </span>
                      </td>
                    </>
                  ) : (
                    <td colSpan={4} className="text-slate-400">{r.cro.destination === 'direct' ? 'direct — no gate-in' : 'not gated in'}</td>
                  )}
                  <td>{r.gateOut ? <span title={r.gateOut.location}>{d(r.gateOut.at)}</span> : none}</td>
                  <td>{r.cro.done ? <span title={r.cro.ref}>{ok('✓')} {r.cro.destination === 'direct' ? 'direct' : r.cro.destination === 'yard' ? 'yard' : ''}</span> : warn('pending')}</td>
                  <td>{r.inStock ? ok(`In stock${r.stockVia ? ` (${r.stockVia})` : ''}`) : none}</td>
                </tr>
                {isOpen && open && (
                  <tr>
                    <td colSpan={20} className="!bg-slate-50 !p-2">
                      {open.kind === 'photos' && sv && (
                        <div className="flex flex-wrap gap-2">
                          {sv.photos.map((p) => (
                            <figure key={p.id} className="m-0 w-[104px]">
                              <Img m={{ path: media(p.id, true), label: p.label }} className="h-[78px] w-[104px] cursor-zoom-in border border-slate-300 object-cover" onClick={() => setBig({ path: media(p.id), label: `${r.containerNo || r.label} — ${p.label}` })} />
                              <figcaption className="mt-0.5 truncate text-[10px] text-slate-600" title={p.label}>{p.label}</figcaption>
                            </figure>
                          ))}
                        </div>
                      )}
                      {open.kind === 'report' && sv?.reportId && <Report m={{ path: media(sv.reportId), label: `${r.containerNo} inspection report` }} />}
                      {open.kind === 'gatephotos' && gi && (
                        <div className="flex flex-wrap gap-2">
                          {gi.photos.map((p, i) => (
                            <figure key={i} className="m-0 w-[104px]">
                              <Img m={{ ...gateMedia(p), label: p.label }} className="h-[78px] w-[104px] cursor-zoom-in border border-slate-300 object-cover" onClick={() => setBig({ ...gateMedia(p), label: `${r.containerNo} — gate-in — ${p.label}` })} />
                              <figcaption className="mt-0.5 truncate text-[10px] text-slate-600" title={p.label}>{p.label}</figcaption>
                            </figure>
                          ))}
                        </div>
                      )}
                      {open.kind === 'gatereport' && gi?.report && <Report m={{ ...gateMedia(gi.report), label: `${r.containerNo} gate-in report` }} />}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}>Total · {totals.containers} containers</td>
            <td colSpan={6}>{totals.surveyed} surveyed{totals.withIssues ? ` · ${totals.withIssues} with issues` : ''}</td>
            <td colSpan={3}>{totals.signedOff} signed off</td>
            <td colSpan={4}>{totals.gatedIn} gated in</td>
            <td>—</td><td>{totals.croDone} CRO</td><td>{totals.inStock} in stock</td>
          </tr>
        </tfoot>
      </table>
      {indentWideApprovals.length > 0 && (
        <div className="border border-t-0 border-slate-300 px-2 py-1 text-[11px] text-slate-600">
          Sign-offs not tied to one container: {indentWideApprovals.map((a, i) => <span key={i} className="mr-2">{/^approved$/i.test(a.status) ? ok('✓') : bad('✗')} {a.stage ? `${a.stage} ` : ''}{a.name}{a.at ? ` ${d(a.at)}` : ''}</span>)}
        </div>
      )}
      {sheetError && <div className="px-2 py-1 text-[11px] text-amber-700">The survey records could not be read just now — survey columns may be blank.</div>}
      {big && <Lightbox m={big} onClose={() => setBig(null)} />}
    </div>
  );
}
